import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {NgClass} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {Subscription, firstValueFrom} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {WorksModel} from "src/app/models/WorksModel";
import {WorksTypes} from "src/app/enums/Works.enum";
import {User} from "src/app/modules/auth/models/User";
import {Friend} from "src/app/modules/friends/interfaces/friend.interface";
import {FriendsService} from "src/app/modules/friends/services/friends.service";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {
  LocalUser,
  WorkCompanion,
  WorkCompanionOptimisticRemoval,
  WorkCompanionWorkWithProgress,
} from "../../interfaces/work-companion.interface";
import {WorksCompanionService} from "../../services/works-companion.service";
import {LocalUserService} from "../../services/local-user.service";
import {getFriendWorksCount} from "src/app/modules/friends/utils/friend-works-count.util";
import {getTopCompanionFriends} from "src/app/modules/friends/utils/friend-companion-rank.util";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {LocalFriendFormComponent} from "src/app/modules/shared/components/local-friend-form/local-friend-form.component";
import {isEmailAddressValid} from "src/app/modules/shared/utils/email.util";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {
  getWorksCompanionFlowTitleKey,
  getWorksCompanionTitleKey,
} from "../../utils/works-companion-title.util";
import {isSeriesWorkType} from "src/app/utils/works-type-guards.util";

@Component({
  selector: "app-works-companion",
  templateUrl: "./works-companion.component.html",
  styleUrl: "./works-companion.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    FormsModule,
    TranslateModule,
    MatSlideToggleModule,
    UserAvatarComponent,
    LocalFriendFormComponent,
  ],
})
export class WorksCompanionComponent implements OnChanges, OnDestroy {
  private static readonly STEP_ONE_MAX_VISIBLE_ROWS = 2;

  @Input() item!: WorksModel;
  @Input() user!: User;
  @Input() mode: "inline" | "modalContent" = "inline";
  @Input() modalIdOverride?: string;

  public companions: WorkCompanion[] = [];
  public friends: Friend[] = [];
  public localUsers: LocalUser[] = [];
  public isLoading = false;
  public modalStep: 1 | 2 | 3 = 1;
  public searchTerm = "";
  public selectedFriendIds: string[] = [];
  public selectedLocalUserIds: string[] = [];
  public syncSelections: Record<string, boolean> = {};
  public newLocalPseudo = "";
  public newLocalEmail = "";
  public sendInvitation = false;
  public isSubmitting = false;
  private invitationToggleTouched = false;
  private lastLoadedKey?: string;
  private hasInlineRefreshSubscription = false;
  private hasOptimisticRemovalSubscription = false;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly worksCompanionService: WorksCompanionService,
    public readonly worksService: WorksService,
    private readonly localUserService: LocalUserService,
    private readonly friendsService: FriendsService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly modalService: ModalService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["item"] || changes["user"]) &&
      this.item?._id &&
      this.user?._id
    ) {
      this.ensureOptimisticRemovalSubscription();
      this.ensureInlineRefreshSubscription();
      this.reloadIfNeeded();
    }
  }

  get modalId(): string {
    return (
      this.modalIdOverride ?? `worksCompanion_${this.item?._id ?? "default"}`
    );
  }

  get titleKey(): string {
    return getWorksCompanionTitleKey(this.item?.type);
  }

  get flowTitleKey(): string {
    return getWorksCompanionFlowTitleKey(this.item?.type);
  }

  get filteredAvailableFriends(): Friend[] {
    const query = this.searchTerm.trim().toLowerCase();

    return this.recentAvailableFriends.filter((friend) => {
      if (!query) {
        return true;
      }

      const displayName =
        friend.displayNameOverride || friend.username || friend.firstname || "";

      return displayName.toLowerCase().includes(query);
    });
  }

  get hasSearchTerm(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  get recentAvailableFriends(): Friend[] {
    return getTopCompanionFriends(
      this.availableFriends,
      this.availableFriends.length
    );
  }

  get visibleRecentFriends(): Friend[] {
    return this.hasSearchTerm
      ? this.filteredAvailableFriends
      : this.filteredAvailableFriends.slice(0, this.maxVisibleItemsPerSection);
  }

  get hiddenRecentFriendsCount(): number {
    if (this.hasSearchTerm) {
      return 0;
    }

    return Math.max(
      this.filteredAvailableFriends.length - this.maxVisibleItemsPerSection,
      0
    );
  }

  get filteredAvailableLocalUsers(): LocalUser[] {
    const query = this.searchTerm.trim().toLowerCase();

    return this.availableLocalUsers.filter((localUser) => {
      if (!query) {
        return true;
      }

      return (localUser.displayNameOverride || localUser.pseudo)
        .toLowerCase()
        .includes(query);
    });
  }

  get visibleLocalUsers(): LocalUser[] {
    return this.hasSearchTerm
      ? this.filteredAvailableLocalUsers
      : this.filteredAvailableLocalUsers.slice(
          0,
          this.maxVisibleItemsPerSection
        );
  }

  get hiddenLocalUsersCount(): number {
    if (this.hasSearchTerm) {
      return 0;
    }

    return Math.max(
      this.filteredAvailableLocalUsers.length - this.maxVisibleItemsPerSection,
      0
    );
  }

  get availableFriends(): Friend[] {
    const linkedIds = new Set(
      this.companions
        .filter(
          (companion) =>
            companion.target.type === "user" &&
            companion.status !== "removed" &&
            companion.status !== "declined"
        )
        .map((companion) => companion.target._id)
    );

    return this.friends.filter((friend) => !linkedIds.has(friend._id));
  }

  get availableLocalUsers(): LocalUser[] {
    const linkedIds = new Set(
      this.companions
        .filter(
          (companion) =>
            companion.target.type === "local" &&
            companion.status !== "removed" &&
            companion.status !== "declined"
        )
        .map((companion) => companion.target._id)
    );

    return this.localUsers.filter((localUser) => !linkedIds.has(localUser._id));
  }

  get selectedFriends(): Friend[] {
    return this.availableFriends.filter((friend) =>
      this.selectedFriendIds.includes(friend._id)
    );
  }

  get selectedLocalUsers(): LocalUser[] {
    return this.availableLocalUsers.filter((localUser) =>
      this.selectedLocalUserIds.includes(localUser._id)
    );
  }

  get hasPendingNewLocalFriend(): boolean {
    return this.newLocalPseudo.trim().length > 0;
  }

  get pendingNewLocalFriendName(): string {
    return this.newLocalPseudo.trim();
  }

  get selectedCount(): number {
    return (
      this.selectedFriendIds.length +
      this.selectedLocalUserIds.length +
      (this.hasPendingNewLocalFriend ? 1 : 0)
    );
  }

  get selectedCountLabel(): string {
    return this.selectedCount === 1
      ? "WORKS_COMPANION_SELECTED_SINGULAR"
      : "WORKS_COMPANION_SELECTED_PLURAL";
  }

  get maxVisibleItemsPerSection(): number {
    return (
      WorksCompanionComponent.STEP_ONE_MAX_VISIBLE_ROWS * this.stepOneColumns
    );
  }

  get stepOneColumns(): number {
    return this.screenSizeService.currentScreenSize.isFullLayoutMobile ? 1 : 2;
  }

  get hasRecentFriendsSection(): boolean {
    return (
      this.visibleRecentFriends.length > 0 || this.hiddenRecentFriendsCount > 0
    );
  }

  get hasLocalFriendsSection(): boolean {
    return this.visibleLocalUsers.length > 0 || this.hiddenLocalUsersCount > 0;
  }

  get shouldShowNoLocalFriends(): boolean {
    return !this.hasSearchTerm && this.visibleLocalUsers.length === 0;
  }

  get canSubmitSelection(): boolean {
    return this.selectedCount > 0;
  }

  get canSubmitNewLocalFriend(): boolean {
    return this.hasPendingNewLocalFriend;
  }

  get isNewLocalEmailValid(): boolean {
    return isEmailAddressValid(this.newLocalEmail);
  }

  get isSyncEligible(): boolean {
    return (
      this.isSeriesType(this.item.type) || this.item.type === WorksTypes.Game
    );
  }

  get defaultSyncEnabled(): boolean {
    return !this.isCompletedWork();
  }

  reload(force = false): void {
    const contextKey = this.getContextKey();
    if (!contextKey) {
      return;
    }

    if (!force && this.lastLoadedKey === contextKey) {
      return;
    }

    this.lastLoadedKey = contextKey;
    this.isLoading = true;
    this.cdr.markForCheck();

    this.worksCompanionService
      .getCompanions(this.user._id, this.item._id)
      .subscribe({
        next: (companions) => {
          this.companions = this.filterRuntimeRemovedCompanions(
            companions ?? []
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.companions = [];
          this.cdr.markForCheck();
        },
      });

    this.friendsService.getFriends(this.user._id, true).subscribe({
      next: (response) => {
        this.friends = ((response?.data as Friend[] | undefined) ?? []).filter(
          (friend) => Boolean(friend._id)
        );
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.friends = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });

    this.localUserService.getLocalUsers(this.user._id).subscribe({
      next: (users) => {
        this.localUsers = users ?? [];
        this.cdr.markForCheck();
      },
      error: () => {
        this.localUsers = [];
        this.cdr.markForCheck();
      },
    });
  }

  openAddModal(): void {
    this.resetFlow();
    this.modalService.openItemModal(this.modalId, this.item);
  }

  closeAddModal(): void {
    this.modalService.closeModal(this.modalId);
    this.resetFlow();
  }

  toggleFriendSelection(friendId: string): void {
    if (this.selectedFriendIds.includes(friendId)) {
      this.selectedFriendIds = this.selectedFriendIds.filter(
        (id) => id !== friendId
      );
    } else {
      this.selectedFriendIds = [...this.selectedFriendIds, friendId];
      this.syncSelections[friendId] =
        this.getDefaultSyncSelectionForFriend(friendId);
    }

    this.cdr.markForCheck();
  }

  toggleLocalUserSelection(localUserId: string): void {
    if (this.selectedLocalUserIds.includes(localUserId)) {
      this.selectedLocalUserIds = this.selectedLocalUserIds.filter(
        (id) => id !== localUserId
      );
    } else {
      this.selectedLocalUserIds = [...this.selectedLocalUserIds, localUserId];
    }

    this.cdr.markForCheck();
  }

  isFriendSelected(friendId: string): boolean {
    return this.selectedFriendIds.includes(friendId);
  }

  isLocalUserSelected(localUserId: string): boolean {
    return this.selectedLocalUserIds.includes(localUserId);
  }

  openNewLocalFriendStep(): void {
    this.modalStep = 2;
    this.cdr.markForCheck();
  }

  backToSelectionStep(): void {
    this.modalStep = 1;
    this.cdr.markForCheck();
  }

  backToSyncSelectionStep(): void {
    this.modalStep = 1;
    this.cdr.markForCheck();
  }

  onLocalEmailInput(value: string): void {
    this.newLocalEmail = value;

    if (!this.isNewLocalEmailValid) {
      this.sendInvitation = false;
      this.invitationToggleTouched = false;
    } else if (!this.invitationToggleTouched) {
      this.sendInvitation = true;
    }

    this.cdr.markForCheck();
  }

  onInvitationToggleChange(value: boolean): void {
    this.invitationToggleTouched = true;
    this.sendInvitation = value;
    this.cdr.markForCheck();
  }

  continueFromSelection(): void {
    if (!this.canSubmitSelection) {
      return;
    }

    if (this.isSyncEligible && this.selectedFriendIds.length > 0) {
      this.modalStep = 3;
      this.cdr.markForCheck();
      return;
    }

    void this.submitSelections();
  }

  continueFromNewFriend(): void {
    if (!this.canSubmitNewLocalFriend) {
      return;
    }

    if (this.isSyncEligible && this.selectedFriendIds.length > 0) {
      this.modalStep = 3;
      this.cdr.markForCheck();
      return;
    }

    void this.submitSelections();
  }

  toggleSyncSelection(friendId: string): void {
    if (!this.canEnableSyncSelection(friendId)) {
      this.syncSelections[friendId] = false;
      this.cdr.markForCheck();
      return;
    }

    this.syncSelections[friendId] = !this.syncSelections[friendId];
    this.cdr.markForCheck();
  }

  async submitSelections(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    if (!this.canSubmitSelection && !this.canSubmitNewLocalFriend) {
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    try {
      const localIds = [...this.selectedLocalUserIds];

      if (this.hasPendingNewLocalFriend) {
        const createdLocalUser = await firstValueFrom(
          this.localUserService.createLocalUser(this.user._id, {
            pseudo: this.newLocalPseudo.trim(),
            email: this.newLocalEmail.trim() || undefined,
            sendInvitation: this.sendInvitation,
            invitationWorkId: this.sendInvitation ? this.item._id : undefined,
            invitationWorkType: this.sendInvitation
              ? this.item.type
              : undefined,
          })
        );
        localIds.push(createdLocalUser._id);
      }

      for (const friendId of this.selectedFriendIds) {
        await this.worksCompanionService.waitForOptimisticRemovalByTarget(
          this.user._id,
          this.item._id,
          "user",
          friendId
        );

        const syncEnabled =
          this.canEnableSyncSelection(friendId) &&
          (this.syncSelections[friendId] ?? this.defaultSyncEnabled);

        await firstValueFrom(
          this.worksCompanionService.addCompanion(
            this.user._id,
            this.item._id,
            this.item.type,
            {
              companionUserId: friendId,
              syncEnabled,
              syncConsent: syncEnabled,
            }
          )
        );

        this.worksCompanionService.clearOptimisticRemovalByTarget(
          this.user._id,
          this.item._id,
          "user",
          friendId
        );
      }

      for (const localUserId of localIds) {
        await this.worksCompanionService.waitForOptimisticRemovalByTarget(
          this.user._id,
          this.item._id,
          "local",
          localUserId
        );

        await firstValueFrom(
          this.worksCompanionService.addCompanion(
            this.user._id,
            this.item._id,
            this.item.type,
            {
              companionLocalId: localUserId,
              syncEnabled: true,
              syncConsent: true,
            }
          )
        );

        this.worksCompanionService.clearOptimisticRemovalByTarget(
          this.user._id,
          this.item._id,
          "local",
          localUserId
        );
      }

      this.worksCompanionService.notifyCompanionsChanged();
      this.closeAddModal();
      this.reload(true);
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  async toggleSync(companion: WorkCompanion): Promise<void> {
    const previousSyncEnabled = companion.syncEnabled;
    const previousSyncConsent = companion.syncConsent;
    const nextSyncEnabled = !previousSyncEnabled;
    if (nextSyncEnabled && !this.canEnableCompanionSync(companion)) {
      companion.syncEnabled = false;
      companion.syncConsent = false;
      this.cdr.markForCheck();
      return;
    }

    companion.syncEnabled = nextSyncEnabled;
    companion.syncConsent = nextSyncEnabled;
    this.cdr.markForCheck();

    try {
      await firstValueFrom(
        this.worksCompanionService.patchCompanion(
          this.user._id,
          this.item._id,
          companion._id,
          {
            syncEnabled: nextSyncEnabled,
            syncConsent: nextSyncEnabled,
          }
        )
      );
      this.reload(true);
    } catch {
      companion.syncEnabled = previousSyncEnabled;
      companion.syncConsent = previousSyncConsent;
      this.cdr.markForCheck();
    }
  }

  async removeCompanion(companion: WorkCompanion): Promise<void> {
    const previousCompanions = [...this.companions];
    const optimisticRemoval: WorkCompanionOptimisticRemoval = {
      userId: this.user._id,
      workId: this.item._id,
      companionId: companion._id,
      targetId: companion.target._id,
      targetType: companion.target.type,
    };
    const removalRequest = firstValueFrom(
      this.worksCompanionService.removeCompanion(
        this.user._id,
        this.item._id,
        companion._id
      )
    ).then(() => undefined);

    this.worksCompanionService.registerOptimisticRemoval({
      ...optimisticRemoval,
      completion: removalRequest,
    });
    this.companions = this.companions.filter(
      (currentCompanion) => currentCompanion._id !== companion._id
    );
    this.cdr.markForCheck();

    try {
      await removalRequest;
      this.worksCompanionService.clearOptimisticRemoval(optimisticRemoval);
      this.worksCompanionService.notifyCompanionsChanged();
      this.reload(true);
    } catch {
      this.worksCompanionService.clearOptimisticRemoval(optimisticRemoval);
      this.companions = previousCompanions;
      this.cdr.markForCheck();
    }
  }

  canToggleSync(companion: WorkCompanion): boolean {
    return this.isSyncEligible && companion.target.type === "user";
  }

  canEnableCompanionSync(companion: WorkCompanion): boolean {
    if (!this.canToggleSync(companion) || this.isCompletedWork()) {
      return false;
    }

    const friend = this.getFriendById(companion.target._id);
    return friend ? !this.isFriendCompletedForCurrentWork(friend) : true;
  }

  isCompanionSyncDisabledBecauseCompleted(companion: WorkCompanion): boolean {
    if (!this.canToggleSync(companion)) {
      return false;
    }

    if (this.isCompletedWork()) {
      return true;
    }

    const friend = this.getFriendById(companion.target._id);
    return friend ? this.isFriendCompletedForCurrentWork(friend) : false;
  }

  isLocalCompanion(companion: WorkCompanion): boolean {
    return companion.target.type === "local";
  }

  getCompanionDisplayName(companion: WorkCompanion): string {
    return companion.target.displayName || companion.target.username || "";
  }

  getSelectedFriendWorksCount(friend: Friend): number {
    return getFriendWorksCount(friend);
  }

  getSelectedFriendWorksCountLabel(friend: Friend): string {
    return this.getSelectedFriendWorksCount(friend) === 1
      ? "WORK_COUNT"
      : "WORKS_COUNT";
  }

  isSyncSelectionEnabled(friendId: string): boolean {
    if (!this.canEnableSyncSelection(friendId)) {
      return false;
    }

    return (
      this.syncSelections[friendId] ??
      this.getDefaultSyncSelectionForFriend(friendId)
    );
  }

  canEnableSyncSelection(friendId: string): boolean {
    if (!this.isSyncEligible || this.isCompletedWork()) {
      return false;
    }

    const friend = this.friends.find((entry) => entry._id === friendId);
    if (!friend) {
      return false;
    }

    return !this.isFriendCompletedForCurrentWork(friend);
  }

  isSyncDisabledBecauseCompleted(friendId: string): boolean {
    if (!this.isSyncEligible) {
      return false;
    }

    if (this.isCompletedWork()) {
      return true;
    }

    const friend = this.friends.find((entry) => entry._id === friendId);
    return friend ? this.isFriendCompletedForCurrentWork(friend) : false;
  }

  private isSeriesType(type: WorksTypes | undefined): boolean {
    return isSeriesWorkType(type);
  }

  private getDefaultSyncSelectionForFriend(friendId: string): boolean {
    if (!this.canEnableSyncSelection(friendId)) {
      return false;
    }

    return this.defaultSyncEnabled;
  }

  private isFriendCompletedForCurrentWork(friend: Friend): boolean {
    if (!this.item?._id) {
      return false;
    }

    switch (this.item.type) {
      case WorksTypes.Series:
      case WorksTypes.Animation:
        return Boolean(
          friend.series?.find((entry) => entry._id === this.item._id)?.progress
            ?.completed
        );
      case WorksTypes.Game: {
        const progress = friend.games?.find(
          (entry) => entry._id === this.item._id
        )?.progress;
        return Boolean(
          progress?.completed || progress?.completion === "completed"
        );
      }
      default:
        return false;
    }
  }

  private isCompletedWork(): boolean {
    const progress = (this.item as WorksModel & WorkCompanionWorkWithProgress)
      .progress;

    return Boolean(progress?.completed || progress?.completion === "completed");
  }

  public resetFlow(): void {
    this.modalStep = 1;
    this.searchTerm = "";
    this.selectedFriendIds = [];
    this.selectedLocalUserIds = [];
    this.syncSelections = {};
    this.newLocalPseudo = "";
    this.newLocalEmail = "";
    this.sendInvitation = false;
    this.invitationToggleTouched = false;
    this.cdr.markForCheck();
  }

  private reloadIfNeeded(): void {
    this.reload(false);
  }

  private ensureInlineRefreshSubscription(): void {
    if (this.mode !== "inline" || this.hasInlineRefreshSubscription) {
      return;
    }

    const modalOpenedSubscription = this.modalService.modalOpened.subscribe(
      (event) => {
        if (
          event?.modalId === this.modalId &&
          !event.opened &&
          this.item?._id &&
          this.user?._id
        ) {
          this.reload(true);
        }
      }
    );

    const companionChangesSubscription =
      this.worksCompanionService.companionChanges$.subscribe(() => {
        if (this.item?._id && this.user?._id) {
          this.reload(true);
        }
      });

    this.subscriptions.push(
      modalOpenedSubscription,
      companionChangesSubscription
    );
    this.hasInlineRefreshSubscription = true;
  }

  private ensureOptimisticRemovalSubscription(): void {
    if (this.hasOptimisticRemovalSubscription) {
      return;
    }

    const optimisticRemovalSubscription =
      this.worksCompanionService.optimisticRemovalChanges$.subscribe(() => {
        this.companions = this.filterRuntimeRemovedCompanions(this.companions);
        this.cdr.markForCheck();
      });

    this.subscriptions.push(optimisticRemovalSubscription);
    this.hasOptimisticRemovalSubscription = true;
  }

  private getContextKey(): string | null {
    if (!this.user?._id || !this.item?._id) {
      return null;
    }

    return `${this.user._id}:${this.item._id}`;
  }

  private getFriendById(friendId: string): Friend | undefined {
    return this.friends.find((entry) => entry._id === friendId);
  }

  private filterRuntimeRemovedCompanions(
    companions: WorkCompanion[]
  ): WorkCompanion[] {
    if (!this.user?._id || !this.item?._id) {
      return companions;
    }

    return companions.filter(
      (companion) =>
        !this.worksCompanionService.isOptimisticallyRemoved(
          this.user._id,
          this.item._id,
          companion
        )
    );
  }
}
